export interface BedroomOption {
  label: string;
  value: string;
}

const BEDROOM_LABELS: Record<number, string> = {
  0: 'Studio',
  1: 'One Bedroom',
  2: 'Two Bedroom',
  3: 'Three Bedroom',
  4: 'Four Bedroom',
  5: 'Five Bedroom',
  6: 'Six Bedroom',
  7: 'Seven Bedroom or more',
};

export const DEFAULT_BEDROOMS: BedroomOption[] = [
  { label: 'Any', value: 'any' },
  { label: 'Studio', value: '0' },
  { label: 'One Bedroom', value: '1' },
  { label: 'Two Bedroom', value: '2' },
  { label: 'Three Bedroom', value: '3' },
  { label: 'Four Bedroom or more', value: 'four_more' },
];

const STUDIO_TOKENS = new Set(['0', 'S', 'STUDIO']);
const BOUND_RE = /^(?:\d+|S|STUDIO)$/i;
const RANGE_RE = /^(\d+|S|STUDIO)\s*-\s*(\d+|S|STUDIO)$/i;

export function parseBedroomCount(raw: string | number | null | undefined): number | null {
  const value = String(raw ?? '').trim().toUpperCase();
  if (!value) return null;
  if (STUDIO_TOKENS.has(value)) return 0;
  const n = parseInt(value, 10);
  if (Number.isNaN(n) || n < 0 || n > 10) return null;
  return n;
}

export function bedroomOptionsFromNumbers(nums: number[]): BedroomOption[] {
  const opts: BedroomOption[] = [{ label: 'Any', value: 'any' }];
  for (const n of nums) {
    const label = BEDROOM_LABELS[n];
    if (label) opts.push({ label, value: String(n) });
  }
  return opts;
}

export function parseBedroomsRange(rangeText: string | null | undefined): BedroomOption[] {
  if (!rangeText || typeof rangeText !== 'string') return DEFAULT_BEDROOMS;
  const trimmed = rangeText.trim();
  if (BOUND_RE.test(trimmed)) {
    const n = parseBedroomCount(trimmed);
    if (n !== null) return bedroomOptionsFromNumbers([n]);
  }
  const match = trimmed.match(RANGE_RE);
  if (!match) return DEFAULT_BEDROOMS;
  const start = parseBedroomCount(match[1]);
  const end = parseBedroomCount(match[2]);
  if (start === null || end === null || end < start) return DEFAULT_BEDROOMS;
  const range: number[] = [];
  for (let i = start; i <= end; i++) range.push(i);
  return bedroomOptionsFromNumbers(range);
}

export function bedroomOptionsFromFloorplans(
  floorplans: Array<{ floorplan_beds_min?: string | number | null }> | null | undefined,
): BedroomOption[] | null {
  if (!floorplans?.length) return null;
  const beds = new Set<number>();
  for (const fp of floorplans) {
    const n = parseBedroomCount(fp.floorplan_beds_min);
    if (n !== null) beds.add(n);
  }
  if (beds.size === 0) return null;
  return bedroomOptionsFromNumbers([...beds].sort((a, b) => a - b));
}
